import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm, NgModelGroup } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { DateTimePreset, ICitationDatesGroup, ISelectOption, TimeframePreset } from 'app/models/filter-form.models';
import { makeControlDirty } from 'app/utils/form.utils';
import { subDays, subWeeks } from 'date-fns';
import moment from 'moment';
import { FilterItemComponent } from '../../filter-item/filter-item.component';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { NgFor } from '@angular/common';

@Component({
  selector: 'app-citation-date-filter',
  templateUrl: './citation-date-filter.component.html',
  styleUrls: ['./citation-date-filter.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [
    FormsModule,
    FilterItemComponent,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatLabel,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatButtonToggleGroup,
    NgFor,
    MatButtonToggle,
  ],
})
export class CitationDateFilterComponent {
  @ViewChild('citationDate') ngModelGroup!: NgModelGroup;

  @Input() set citationDates({ from, to }: { from?: Date | string; to?: Date | string }) {
    this.citationDateGroup = {
      fromDate: from,
      toDate: to,
    };
  }

  @Input() set dateTimePreset(value: DateTimePreset | undefined) {
    this.selectedDateTimePreset = value;
  }

  @Input() set timeframePreset(value: TimeframePreset | undefined) {
    if (value) {
      makeControlDirty(this.ngModelGroup.control);
    }
  }

  @Output() changeDateTime: EventEmitter<{
    fromDate?: Date | string;
    toDate?: Date | string;
    dateTimePreset?: DateTimePreset;
  }> = new EventEmitter();

  citationDateGroup: ICitationDatesGroup = {
    fromDate: undefined,
    toDate: undefined,
  };

  selectedDateTimePreset?: DateTimePreset;
  dateTimePresetOptions: ISelectOption[] = [
    { value: DateTimePreset.TODAY, viewValue: 'Today' },
    { value: DateTimePreset.YESTERDAY, viewValue: 'Yesterday' },
    { value: DateTimePreset.LAST_3_DAYS, viewValue: 'Last 3 days' },
    { value: DateTimePreset.LAST_WEEK, viewValue: 'Last week' },
  ];

  today = new Date();

  get dateRange(): string | undefined {
    const { fromDate, toDate } = this.citationDateGroup;

    const fromDateValue = fromDate ? moment(fromDate).format('YYYY-MM-DD') : null;
    const toDateValue = toDate ? moment(toDate).format('YYYY-MM-DD') : null;

    return fromDateValue && toDateValue ? `${fromDateValue} - ${toDateValue}` : undefined;
  }

  onDateChange(value: Date, filter: 'fromDate' | 'toDate'): void {
    if (filter === 'fromDate') {
      this.citationDateGroup.fromDate = value;
    } else {
      this.citationDateGroup.toDate = value;
    }

    this.selectedDateTimePreset = undefined;
    this.onDateTimeChange();
  }

  onDateTimePresetChange(event: MatButtonToggleChange): void {
    const currentDate = new Date();

    const { value } = event;
    switch (value) {
      case DateTimePreset.TODAY:
        this.applyDatePreset(currentDate, currentDate);
        break;
      case DateTimePreset.YESTERDAY:
        this.applyDatePreset(subDays(currentDate, 1), subDays(currentDate, 1));
        break;
      case DateTimePreset.LAST_3_DAYS:
        this.applyDatePreset(subDays(currentDate, 2), currentDate);
        break;
      case DateTimePreset.LAST_WEEK:
        this.applyDatePreset(subWeeks(currentDate, 1), currentDate);
        break;
    }

    this.onDateTimeChange();
    makeControlDirty(this.ngModelGroup.control);
  }

  clearFilter = (): void => {
    this.citationDateGroup.fromDate = undefined;
    this.citationDateGroup.toDate = undefined;

    this.selectedDateTimePreset = undefined;

    this.onDateTimeChange();
    makeControlDirty(this.ngModelGroup.control);
  };

  private applyDatePreset(fromDate: Date, toDate: Date): void {
    this.parseDates({ fromDate, toDate });
  }

  private parseDates({ fromDate, toDate }: { fromDate?: Date; toDate?: Date }): void {
    if (fromDate) {
      this.citationDateGroup.fromDate = fromDate;
    }
    if (toDate) {
      this.citationDateGroup.toDate = toDate;
    }
  }

  private onDateTimeChange(): void {
    this.changeDateTime.emit({
      fromDate: this.citationDateGroup.fromDate,
      toDate: this.citationDateGroup.toDate,
      dateTimePreset: this.selectedDateTimePreset,
    });
  }
}
